import React, { useMemo } from 'react';
import { MultiSelect } from 'primereact/multiselect';
import { useTranslation } from 'react-i18next';
import { useAccount } from '../../contexts/account/AccountContexts';
import { getProjectsFromAccount } from '../../helpers/project/ProjectHelper';

interface GuestProjectsSelectProps {
  /** Id projektów w zakresie gościa. */
  value: number[];
  onChange: (projectIds: number[]) => void;
  /** Wartość jeszcze się wczytuje (przy edycji zakres przychodzi z osobnego żądania). */
  isValueLoading?: boolean;
}

/**
 * Wybór projektów dla gościa — wspólny dla zaproszenia i późniejszej edycji zakresu, żeby reguła
 * „co wolno wybrać" istniała w jednym miejscu.
 *
 * Lista pochodzi z kontekstu konta, a nie z własnego żądania: panel i tak ją wczytuje przy starcie,
 * więc nie ma po co pytać drugi raz — a każde dodatkowe żądanie to kolejny sposób, żeby wybierak
 * został pusty bez żadnego komunikatu.
 *
 * Zawężenie: `getProjectsFromAccount` (ta sama reguła, co w menu i na liście projektów) plus
 * odsianie projektów DEMO. Demo należy do cudzego konta, więc `validateGuestScope` odrzuca je na
 * backendzie — wybór, który kończy się błędem dopiero przy zapisie, jest gorszy niż jego brak.
 *
 * Wcześniej stało tu porównanie `project.accountId === accountResponse.accountId`. Dla superadmina
 * lista projektów to WSZYSTKIE projekty w systemie, więc taki filtr potrafił zostawić puste pole —
 * i stąd „czasami projekty się nie ładują".
 */
const GuestProjectsSelect: React.FC<GuestProjectsSelectProps> = ({
  value,
  onChange,
  isValueLoading = false,
}) => {
  const { t } = useTranslation();
  const { accountResponse, projectResponse } = useAccount();

  const options = useMemo(
    () =>
      getProjectsFromAccount(accountResponse, projectResponse ?? [])
        .filter((project) => !project.demoVersion)
        .map((project) => ({ id: project.id, name: project.projectLabel })),
    [accountResponse, projectResponse]
  );

  // Etykietę pola PrimeReact składa, szukając KAŻDEJ wartości w `options` — dla id spoza listy
  // dostaje `null` i dokładnie to wypisuje. Przy edycji zakresu wartość przychodzi z osobnego
  // żądania i bywa gotowa przed listą projektów, więc do czasu jej załadowania pokazujemy pusty
  // wybór i blokujemy pole. Sama `value` nie jest ruszana — wraca w całości, gdy opcje dojadą.
  // Kręciołek (`loading`) jest tu po to, żeby zablokowane, puste pole nie wyglądało na zacięte.
  const isLoading = projectResponse === null || isValueLoading;

  return (
    <MultiSelect
      value={isLoading ? [] : value}
      onChange={(e) => onChange(e.value as number[])}
      options={options}
      disabled={isLoading}
      loading={isLoading}
      // Własny spinner, bo bazowy arkusz PrimeReacta (z `@keyframes p-icon-spin`) nie jest w panelu
      // wpinany — domyślna ikonka wisiałaby nieruchomo, czyli dokładnie tak, jak wygląda zacięcie.
      loadingIcon={<span className="ico--loader ico--xs" />}
      className="dropdown dropdown--multi-select"
      optionLabel="name"
      optionValue="id"
      placeholder={isLoading ? t('loadingDataPleaseWait') : t('guestSelectProjects')}
      maxSelectedLabels={2}
      selectedItemsLabel={t('guestProjectsSelected')}
      // Zaznacz-wszystko renderuje się domyślnie, a `assets/scss/multiselect.scss` stylizuje w
      // nagłówku wyłącznie wyszukiwarkę — kontrolka wychodzi surowa. Wszystkie pozostałe
      // `MultiSelect`y w panelu wyłączają ją tak samo.
      showSelectAll={false}
      filter
      filterBy="name"
    />
  );
};

export default GuestProjectsSelect;
